import { ReactNode, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Location, useLocation, useParams } from 'react-router-dom';
import { AuthProvider, RESET_PASSWORD_PATH, useAuth } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { ChatSessionProvider } from './contexts/ChatSessionContext';
import { getCollection, legacyRedirectFor } from './utils/institutions';
import { lazyConRecarga } from './utils/lazyConRecarga';
import ProtectedRoute from './components/auth/ProtectedRoute';
import RestablecerContrasena from './pages/RestablecerContrasena';
import Favoritos from './pages/Favoritos';
import DocumentDetail from './pages/DocumentDetail';
import VentanaDocumento from './components/documento/VentanaDocumento';
import { AnimatePresence } from 'motion/react';
import Admin from './pages/Admin';
import Perfil from './pages/Perfil';
import Notificaciones from './pages/Notificaciones';
import BusquedaInteligente from './pages/BusquedaInteligente';
import Cobertura from './pages/Cobertura';

// Login y Landing cargan aparte: arrastran `motion`, que el chat no usa. Si el chunk falla (despliegue nuevo con
// la pestaña abierta), se recarga la página una sola vez.
const Login = lazyConRecarga(() => import('./pages/Login'), 'login');
const LandingPage = lazyConRecarga(() => import('./pages/LandingPage'), 'landing');
// Bibliotecas y Biblioteca (rediseño, 15-10) también usan `motion`: cargan aparte por la misma razón.
const Bibliotecas = lazyConRecarga(() => import('./pages/Bibliotecas'), 'bibliotecas');
const InstitutionLibrary = lazyConRecarga(() => import('./pages/InstitutionLibrary'), 'biblioteca');

// Ruta antigua: conserva `?q=` para que el chat reciba la pregunta.
export function RedirectToChat() {
  const { search } = useLocation();
  return <Navigate to={`/chat${search}`} replace />;
}

// /biblioteca/:collection → la biblioteca de esa colección. Las direcciones antiguas
// (/biblioteca/sii, /tta, /bcn en cualquier caja) redirigen a su colección conservando `?q=` y demás.
export function LibraryRoute() {
  const { collection: param } = useParams<{ collection: string }>();
  const { search } = useLocation();
  const collection = getCollection(param);
  // `/biblioteca/ADM` se normaliza a la clave en minúscula (la barra lateral marca la activa por la URL).
  if (collection && param !== collection.key) return <Navigate to={`/biblioteca/${collection.key}${search}`} replace />;
  if (collection) return <Suspense fallback={null}><InstitutionLibrary key={collection.key} collection={collection} /></Suspense>;
  const legacy = legacyRedirectFor(param);
  if (legacy) return <Navigate to={`/biblioteca/${legacy}${search}`} replace />;
  return <Navigate to="/bibliotecas" replace />;
}

// La lista de conversaciones vive sobre las rutas y se reinicia al cambiar de usuario
// (cerrar sesión y entrar con otra cuenta en la misma pestaña no debe mostrar la lista anterior).
function ChatSessionScope({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  return <ChatSessionProvider key={user?.id ?? 'anon'}>{children}</ChatSessionProvider>;
}

/**
 * Rutas de la app. Un documento abierto desde una lista (`state.fondo`, DESIGN.md §11.4) se dibuja en la ventana
 * encima de esa lista, que queda montada y en su posición; la misma URL sin fondo es la página del documento.
 */
function Rutas() {
  const location = useLocation();
  const fondo = (location.state as { fondo?: Location } | null)?.fondo;
  return (
    <>
      <Routes location={fondo || location}>
        <Route
          path="/"
          element={
            <Suspense fallback={null}>
              <LandingPage />
            </Suspense>
          }
        />
        <Route
          path="/login"
          element={
            <Suspense fallback={null}>
              <Login />
            </Suspense>
          }
        />
        <Route path={RESET_PASSWORD_PATH} element={<RestablecerContrasena />} />
        <Route path="/cobertura" element={<Cobertura />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Navigate to="/chat" replace />
            </ProtectedRoute>
          }
        />
        <Route
          path="/bibliotecas"
          element={
            <ProtectedRoute>
              <Suspense fallback={null}><Bibliotecas /></Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/biblioteca/:collection"
          element={
            <ProtectedRoute>
              <LibraryRoute />
            </ProtectedRoute>
          }
        />
        <Route
          path="/favoritos"
          element={
            <ProtectedRoute>
              <Favoritos />
            </ProtectedRoute>
          }
        />
        <Route
          path="/documento/:id"
          element={
            <ProtectedRoute>
              <DocumentDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute adminOnly>
              <Admin />
            </ProtectedRoute>
          }
        />
        <Route
          path="/perfil"
          element={
            <ProtectedRoute>
              <Perfil />
            </ProtectedRoute>
          }
        />
        <Route
          path="/notificaciones"
          element={
            <ProtectedRoute>
              <Notificaciones />
            </ProtectedRoute>
          }
        />
        <Route
          path="/busqueda-inteligente"
          element={
            <ProtectedRoute>
              <RedirectToChat />
            </ProtectedRoute>
          }
        />
        <Route
          path="/chat"
          element={
            <ProtectedRoute>
              <BusquedaInteligente />
            </ProtectedRoute>
          }
        />
      </Routes>
      {/* La ventana sale por el mismo camino por el que entró: AnimatePresence la deja montada mientras se funde,
          con la URL que tenía al cerrarse. */}
      <AnimatePresence>
        {fondo && (
          <Routes location={location} key="ventana">
            <Route path="/documento/:id" element={<ProtectedRoute><VentanaDocumento /></ProtectedRoute>} />
          </Routes>
        )}
      </AnimatePresence>
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          {/* Una sola lista de conversaciones para todas las pestañas: la barra lateral es la misma en todas. */}
          <ChatSessionScope>
          <Rutas />
          </ChatSessionScope>
        </BrowserRouter>
      </ToastProvider>
    </AuthProvider>
  );
}

export default App;